import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  REGISTRANT_STATUSES,
  type CpdEntry,
  type Page,
  type Profession,
  type ProfessionalRegistrant,
  type VerifyProfessionalResult,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/** Extracts "NURSE" out of a "REGISTRY_BOARD:NURSE" role string, if present. */
function registryBoardCode(roles: string[] | undefined): string | null {
  const role = roles?.find((r) => r.startsWith('REGISTRY_BOARD:'));
  return role ? role.split(':')[1]! : null;
}

/**
 * The multi-profession registry engine: profession configuration
 * (routes/professions.ts), the registrant lifecycle + CPD ledger
 * (routes/professionalRegister.ts), and the consent-gated verify lookup
 * (routes/verify.ts). SQA_ASSESSOR administers the profession catalogue and
 * can manage any registrant; a REGISTRY_BOARD:<professionCode> persona is
 * scoped to registrants of that one profession only (the backend 403s any
 * other professionCode for that role).
 */
export function ProfessionalRegister() {
  const { persona } = useAuth();
  const isAssessor = persona?.roles.includes('SQA_ASSESSOR') ?? false;
  const boardCode = registryBoardCode(persona?.roles);
  const canManageRegister = isAssessor || boardCode !== null;
  const [tab, setTab] = useState<'professions' | 'registrants' | 'verify'>(isAssessor ? 'professions' : 'registrants');

  return (
    <div>
      <PageHeader title="Professional Register" subtitle="Configurable multi-profession registry — CPD ledger + audit sampling." />
      <Tabs
        tabs={[
          { key: 'professions', label: 'Professions' },
          { key: 'registrants', label: 'Registrants' },
          { key: 'verify', label: 'Verify Licence' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {tab === 'professions' ? (
        <ProfessionsTab isAssessor={isAssessor} />
      ) : tab === 'registrants' ? (
        <RegistrantsTab isAssessor={isAssessor} boardCode={boardCode} canManage={canManageRegister} />
      ) : (
        <VerifyTab canManage={canManageRegister} />
      )}
    </div>
  );
}

function ProfessionsTab({ isAssessor }: { isAssessor: boolean }) {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<Profession>>(`/api/v1/m29/professions?${qs.toString()}`);

  return (
    <div>
      {!isAssessor ? (
        <div className="mb-4">
          <ErrorNote message="Configuring professions requires the SQA Assessor role — the catalogue below is visible to everyone." />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <input className="field-input !w-48" placeholder="Search code / name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="RETIRED">RETIRED</option>
          </select>
        </div>
        {isAssessor ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + Configure profession
          </button>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No professions configured yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Name</th>
                <th className="table-head">Licence classes</th>
                <th className="table-head text-right">CPD pts / cycle</th>
                <th className="table-head text-right">Registration fee</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((p) => (
                <tr key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(p.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{p.code}</td>
                  <td className="table-cell">{p.name}</td>
                  <td className="table-cell text-xs text-slate-500">{p.licenceClasses.join(', ')}</td>
                  <td className="table-cell text-right tabular-nums">
                    {p.cpdRequiredPoints} / {p.cpdCycleMonths}mo
                  </td>
                  <td className="table-cell text-right tabular-nums">{scr(p.registrationFeeAmount)}</td>
                  <td className="table-cell">
                    <StatusBadge status={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateProfessionModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? (
        <ProfessionDetailModal id={detailId} isAssessor={isAssessor} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function CreateProfessionModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [licenceClasses, setLicenceClasses] = useState('');
  const [cpdCycleMonths, setCpdCycleMonths] = useState('24');
  const [cpdRequiredPoints, setCpdRequiredPoints] = useState('40');
  const [renewalPeriodMonths, setRenewalPeriodMonths] = useState('24');
  const [registrationFeeAmount, setRegistrationFeeAmount] = useState('');
  const [renewalFeeAmount, setRenewalFeeAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m29/professions', {
        code: code.toUpperCase(),
        name,
        licenceClasses: licenceClasses
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        cpdCycleMonths: Number.parseInt(cpdCycleMonths, 10),
        cpdRequiredPoints: Number.parseInt(cpdRequiredPoints, 10),
        renewalPeriodMonths: Number.parseInt(renewalPeriodMonths, 10),
        registrationFeeAmount: Number.parseFloat(registrationFeeAmount),
        renewalFeeAmount: Number.parseFloat(renewalFeeAmount),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to configure profession');
    } finally {
      setBusy(false);
    }
  };

  const validCode = /^[A-Z0-9_]{2,40}$/.test(code.toUpperCase());

  return (
    <Modal title="Configure profession" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code (UPPER_SNAKE_CASE)">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. NURSING" />
          </Field>
          <Field label="Name">
            <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nursing & Midwifery" />
          </Field>
        </div>
        <Field label="Licence classes (comma separated)">
          <input className="field-input" value={licenceClasses} onChange={(e) => setLicenceClasses(e.target.value)} placeholder="e.g. REGISTERED_NURSE, MIDWIFE" />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="CPD cycle (months)">
            <input className="field-input" type="number" min="1" value={cpdCycleMonths} onChange={(e) => setCpdCycleMonths(e.target.value)} />
          </Field>
          <Field label="CPD required points">
            <input className="field-input" type="number" min="0" value={cpdRequiredPoints} onChange={(e) => setCpdRequiredPoints(e.target.value)} />
          </Field>
          <Field label="Renewal period (months)">
            <input className="field-input" type="number" min="1" value={renewalPeriodMonths} onChange={(e) => setRenewalPeriodMonths(e.target.value)} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Registration fee (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={registrationFeeAmount} onChange={(e) => setRegistrationFeeAmount(e.target.value)} />
          </Field>
          <Field label="Renewal fee (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={renewalFeeAmount} onChange={(e) => setRenewalFeeAmount(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !validCode || name.trim().length < 2 || !licenceClasses.trim() || !registrationFeeAmount || !renewalFeeAmount}
          >
            {busy ? 'Configuring…' : 'Configure profession'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProfessionDetailModal({ id, isAssessor, onClose, onChanged }: { id: string; isAssessor: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<Profession>(`/api/v1/m29/professions/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const profession = detailQ.data;

  const retire = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/professions/${id}/retire`);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not retire profession');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Profession detail" onClose={onClose}>
      {detailQ.loading || !profession ? (
        <Spinner />
      ) : (
        <div className="space-y-3 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={profession.status} />
            <span className="font-mono text-xs font-semibold">{profession.code}</span>
          </div>
          <dl className="space-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Name</dt>
              <dd className="font-medium">{profession.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Licence classes</dt>
              <dd className="font-medium">{profession.licenceClasses.join(', ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">CPD requirement</dt>
              <dd className="font-medium">
                {profession.cpdRequiredPoints} pts / {profession.cpdCycleMonths} months
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Renewal period</dt>
              <dd className="font-medium">{profession.renewalPeriodMonths} months</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Registration fee</dt>
              <dd className="font-medium">{scr(profession.registrationFeeAmount)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Renewal fee</dt>
              <dd className="font-medium">{scr(profession.renewalFeeAmount)}</dd>
            </div>
          </dl>
          {isAssessor && profession.status === 'ACTIVE' ? (
            <div className="border-t border-slate-100 pt-3">
              <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={busy} onClick={retire}>
                Retire profession (closes registry to new registrants)
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function RegistrantsTab({ isAssessor, boardCode, canManage }: { isAssessor: boolean; boardCode: string | null; canManage: boolean }) {
  const [professionCode, setProfessionCode] = useState(boardCode ?? '');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const effectiveProfessionCode = boardCode ?? professionCode;
  const qs = new URLSearchParams({ limit: '50' });
  if (effectiveProfessionCode) qs.set('professionCode', effectiveProfessionCode);
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const canQuery = isAssessor || Boolean(boardCode);
  const listQ = useApiQuery<Page<ProfessionalRegistrant>>(canQuery ? `/api/v1/m29/professional-register?${qs.toString()}` : null);
  const professionsQ = useApiQuery<Page<Profession>>('/api/v1/m29/professions?limit=100');

  if (!canManage) {
    return <ErrorNote message="Listing registrants requires the SQA Assessor role, or a Registry Board seat scoped to one profession." />;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-52" placeholder="Search NIN / name / licence…" value={q} onChange={(e) => setQ(e.target.value)} />
        {boardCode ? (
          <span className="field-input !w-48 !cursor-default bg-slate-50 text-slate-500">Profession: {boardCode}</span>
        ) : (
          <select className="field-input !w-48" value={professionCode} onChange={(e) => setProfessionCode(e.target.value)}>
            <option value="">All professions</option>
            {professionsQ.data?.items.map((p) => (
              <option key={p.code} value={p.code}>
                {p.code}
              </option>
            ))}
          </select>
        )}
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {REGISTRANT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No registrants match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Registrant</th>
                <th className="table-head">Profession</th>
                <th className="table-head">Licence class</th>
                <th className="table-head">Licence no.</th>
                <th className="table-head">Expires</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(r.id)}>
                  <td className="table-cell">
                    <div className="font-medium">{r.fullName}</div>
                    <div className="font-mono text-xs text-slate-400">{r.registrantNin}</div>
                  </td>
                  <td className="table-cell font-mono text-xs">{r.professionCode}</td>
                  <td className="table-cell text-xs text-slate-500">{r.licenceClass}</td>
                  <td className="table-cell font-mono text-xs">{r.licenceNo ?? '—'}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(r.expiresAt)}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? <RegistrantDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function RegistrantDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<ProfessionalRegistrant>(`/api/v1/m29/professional-register/${id}`);
  const cpdQ = useApiQuery<{ items: CpdEntry[] }>(`/api/v1/m29/professional-register/${id}/cpd`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showReasonFor, setShowReasonFor] = useState<'reject' | 'suspend' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');
  const [auditingId, setAuditingId] = useState<string | null>(null);
  const [auditOutcome, setAuditOutcome] = useState<'PASS' | 'FAIL'>('PASS');
  const [auditNotes, setAuditNotes] = useState('');

  const registrant = detailQ.data;

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/professional-register/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowReasonFor(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const actCpd = async (entryId: string, path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m29/professional-register/${id}/cpd/${entryId}${path}`, body);
      cpdQ.reload();
      setAuditingId(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Professional registrant" onClose={onClose} wide>
      {detailQ.loading || !registrant ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={registrant.status} />
            <span className="font-semibold">{registrant.fullName}</span>
            <span className="font-mono text-xs text-slate-400">{registrant.registrantNin}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Profession</dt>
              <dd className="font-medium">
                {registrant.professionName} <span className="font-mono text-xs text-slate-400">({registrant.professionCode})</span>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Licence class</dt>
              <dd className="font-medium">{registrant.licenceClass}</dd>
            </div>
            {registrant.licenceNo ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Licence no.</dt>
                <dd className="font-mono text-xs">{registrant.licenceNo}</dd>
              </div>
            ) : null}
            <div className="flex justify-between">
              <dt className="text-slate-500">Fee paid</dt>
              <dd>
                <StatusBadge status={registrant.feePaid ? 'PAID' : 'PENDING'} />
              </dd>
            </div>
            {registrant.expiresAt ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Expires</dt>
                <dd className="font-medium">{formatDate(registrant.expiresAt)}</dd>
              </div>
            ) : null}
          </dl>
          {registrant.statusReason ? <p className="text-xs text-slate-500">Reason: {registrant.statusReason}</p> : null}

          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">CPD ledger</div>
            {cpdQ.loading ? (
              <Spinner />
            ) : (cpdQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-xs text-slate-400">No CPD activity logged yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {cpdQ.data!.items.map((e) => (
                  <li key={e.id} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs">
                    <div className="flex items-center justify-between">
                      <span>
                        {e.activity} — {e.points} pts
                      </span>
                      <span className="flex items-center gap-1.5">
                        {e.auditSampled ? <StatusBadge status={e.auditOutcome ?? 'PENDING'} /> : null}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-slate-400">
                      <span>{formatDate(e.loggedAt)}</span>
                      {!e.auditSampled ? (
                        <button className="btn-secondary !px-2 !py-0.5 text-[11px]" disabled={busy} onClick={() => actCpd(e.id, '/sample')}>
                          Sample for audit
                        </button>
                      ) : !e.auditOutcome ? (
                        auditingId === e.id ? (
                          <span className="flex items-center gap-1.5">
                            <select className="field-input !w-24 !py-0.5 text-[11px]" value={auditOutcome} onChange={(ev) => setAuditOutcome(ev.target.value as 'PASS' | 'FAIL')}>
                              <option value="PASS">PASS</option>
                              <option value="FAIL">FAIL</option>
                            </select>
                            <input
                              className="field-input !w-32 !py-0.5 text-[11px]"
                              placeholder="Notes"
                              value={auditNotes}
                              onChange={(ev) => setAuditNotes(ev.target.value)}
                            />
                            <button
                              className="btn-primary !px-2 !py-0.5 text-[11px]"
                              disabled={busy}
                              onClick={() => actCpd(e.id, '/audit-outcome', { outcome: auditOutcome, notes: auditNotes || undefined })}
                            >
                              Save
                            </button>
                          </span>
                        ) : (
                          <button className="btn-secondary !px-2 !py-0.5 text-[11px]" onClick={() => setAuditingId(e.id)}>
                            Record audit outcome
                          </button>
                        )
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {registrant.status === 'PENDING_VERIFICATION' ? (
              <>
                <button
                  className="btn-success !px-2.5 !py-1 text-xs"
                  disabled={busy || !registrant.feePaid}
                  title={!registrant.feePaid ? 'Registration fee not confirmed yet' : undefined}
                  onClick={() => act('/approve')}
                >
                  Approve
                </button>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('reject')}>
                  Reject
                </button>
              </>
            ) : null}
            {registrant.status === 'REGISTERED' ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('suspend')}>
                Suspend
              </button>
            ) : null}
            {registrant.status === 'SUSPENDED' ? (
              <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/reinstate')}>
                Reinstate
              </button>
            ) : null}
            {registrant.status === 'REGISTERED' && registrant.expiresAt && new Date(registrant.expiresAt).getTime() <= Date.now() ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/mark-lapsed')}>
                Mark lapsed
              </button>
            ) : null}
            {['REGISTERED', 'SUSPENDED', 'LAPSED'].includes(registrant.status) ? (
              <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowReasonFor('revoke')}>
                Revoke
              </button>
            ) : null}
          </div>

          {showReasonFor ? (
            <div className="flex items-end gap-2 rounded-lg border border-slate-200 p-3">
              <Field label="Reason" className="!mb-0 flex-1">
                <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={() => act(`/${showReasonFor}`, { reason })}>
                Confirm
              </button>
              <button className="btn-secondary" onClick={() => setShowReasonFor(null)}>
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}

function VerifyTab({ canManage }: { canManage: boolean }) {
  const [licenceNo, setLicenceNo] = useState('');
  const [result, setResult] = useState<VerifyProfessionalResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.get<VerifyProfessionalResult>(`/api/v1/m29/verify/professional/${encodeURIComponent(licenceNo)}`);
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Verification failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">sqa:verify-professional</h2>
      <p className="mb-4 text-xs text-slate-500">
        {canManage
          ? 'Staff lookup — allowed outright for SQA Assessor / your Registry Board seat.'
          : 'Any other caller requires an affirmative C7 ConsentHub check for this registrant first.'}
      </p>
      {error ? <ErrorNote message={error} /> : null}
      <div className="flex items-end gap-2">
        <Field label="Licence number" className="!mb-0 flex-1">
          <input className="field-input" value={licenceNo} onChange={(e) => setLicenceNo(e.target.value)} placeholder="e.g. NUR-000123" />
        </Field>
        <button className="btn-primary" disabled={busy || licenceNo.trim().length < 3} onClick={check}>
          {busy ? 'Checking…' : 'Check'}
        </button>
      </div>
      {result ? (
        <div className="mt-4 border-t border-slate-100 pt-3">
          {!result.found ? (
            <p className="text-sm text-slate-500">No registrant found for that licence number.</p>
          ) : (
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">Valid</dt>
                <dd>
                  <StatusBadge status={result.valid ? 'VALID' : (result.status ?? 'REVOKED')} />
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Name</dt>
                <dd className="font-medium">{result.fullName}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Profession</dt>
                <dd className="font-medium">{result.professionName}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Licence class</dt>
                <dd className="font-medium">{result.licenceClass}</dd>
              </div>
            </dl>
          )}
        </div>
      ) : null}
    </div>
  );
}
